/**
 * @file        src/api/flightBookings.js
 * @description Typed admin API client for per-booking B2C Flight actions:
 *              details, DB-only update, and supplier cancellation (proxied).
 * @author      Kinetika Labs
 * @created     2026-07-09
 * @changelog
 *   - 2026-08-04 Claude — CR D2: added shareByEmail (Ticket/Invoice/Both, with
 *                          real PDF attachments built client-side by
 *                          ShareEmailModal).
 *   - 2026-08-04 Claude — Added getBookingLogs (per-booking Timeline card).
 *   - 2026-07-19 Claude — Added amendment queue client: listAmendments (Cart
 *                          Amendments card) + raiseEmailAmendment (email-only types).
 *   - 2026-07-09 Claude — Initial (Booking List row actions).
 */
import { get, patch, post } from './client';

export const getBookingDetails = (id) => get(`/flight/bookings/${id}/details`);

export const updateBooking = (id, body) => patch(`/flight/bookings/${id}`, body);

export const cancelPreview = (id) => post(`/flight/bookings/${id}/cancel/preview`, {});

export const cancelSubmit = (id, reason) =>
  post(`/flight/bookings/${id}/cancel/submit`, { reason });

// Amendments raised against a booking (for the Cart Amendments card).
export const listAmendments = (id) => get(`/flight/bookings/${id}/amendments`);

// Operational history of one booking — the server-composed `timeline` array
// (created / payment / amendment / cancellation / refund lifecycle / admin
// edits, each with its actor and its before → after field changes). Powers the
// Timeline card in the Details drawer.
export const getBookingLogs = (id) => get(`/flight/bookings/${id}/logs`);

// Raise an email-only amendment (Fare Change, Correction, No Show, …). The server
// records it and notifies the ops team; the resolved value reports { emailed }.
export const raiseEmailAmendment = (id, body) =>
  post(`/flight/bookings/${id}/amendment/email`, body);

// Void / Full Refund — supplier charges→submit (proxied to the booking backend).
// preview is a read-only refund quote; submit commits it.
export const amendmentPreview = (id, type) =>
  post(`/flight/bookings/${id}/amendment/preview`, { type });

export const amendmentSubmit = (id, type, reason) =>
  post(`/flight/bookings/${id}/amendment/submit`, { type, reason });

// Email the E-Ticket / Tax Invoice / Both, with real PDF attachment(s), to the
// customer or an override + CC address (CR D2). `body` = { document, to, cc,
// attachments: [{ filename, contentBase64, documentType }] }.
export const shareByEmail = (id, body) =>
  post(`/flight/bookings/${id}/share-email`, body);
